import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  useMediaQuery,
  Tab,
  Tabs,
} from '@mui/material';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  PageTitle,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import MeetingsCalendar from '../../components/MeetingsCalendar';
import ExpertMeetingSettings from '../../components/expert/ExpertMeetingSettings';
import { format } from 'date-fns';
import { Meeting } from '../../types/meetings';
import { useExpert } from '../../redux/slices/expert/useExpert';
import { useUser } from '../../redux/slices/user/useUser';

const currentTeam = "Wanderlust Adventures";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`meetings-tabpanel-${index}`}
      aria-labelledby={`meetings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MeetingsPage: React.FC = () => {
  const { body: currentUser } = useUser()
  const { body: expert } = useExpert()
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isNewMeetingDialogOpen, setIsNewMeetingDialogOpen] = useState(false);
  const [newMeeting, setNewMeeting] = useState<Partial<Meeting>>({});
  const [currentTab, setCurrentTab] = useState(0);
  const [settings, setSettings] = useState({
    timezone: 'Australia/Melbourne',
    workingHours: [{
      start: '09:00',
      end: '17:00',
      daysOfWeek: [1, 2, 3, 4, 5],
    }],
  });
  const isMobile = useMediaQuery('(max-width: 768px)');

  const userName = `${currentUser?.first_name} ${currentUser?.last_name}`

  const handleSelectMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
  };

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    setNewMeeting({
      start,
      end,
      title: '',
      description: '',
      owner: userName,
      status: 'scheduled'
    });
    setIsNewMeetingDialogOpen(true);
  };

  const handleCreateMeeting = () => {
    if (expert && newMeeting.title && newMeeting.start && newMeeting.end) {
      const meeting: Meeting = {
        id: Date.now().toString(),
        expert_id: expert?.id,
        owner: userName,
        title: newMeeting.title,
        start: newMeeting.start,
        end: newMeeting.end,
        description: newMeeting.description,
        status: 'scheduled'
      };
      setMeetings([...meetings, meeting]);
      setIsNewMeetingDialogOpen(false);
      setNewMeeting({});
    }
  };

  const handleSettingsSave = async (newSettings: any) => {
    // Replace with actual API call
    setSettings(newSettings);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Scheduled Meetings</PageTitle>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Calendar" />
              <Tab label="Settings" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <MeetingsCalendar
              meetings={meetings}
              currentUser={userName}
              onSelectMeeting={handleSelectMeeting}
              onSelectSlot={handleSelectSlot}
            />
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <ExpertMeetingSettings
              initialSettings={settings}
              onSave={handleSettingsSave}
            />
          </TabPanel>

          {/* Meeting Details Dialog */}
          <Dialog
            open={!!selectedMeeting}
            onClose={() => setSelectedMeeting(null)}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
          >
            {selectedMeeting && (
              <>
                <DialogTitle>{selectedMeeting.title}</DialogTitle>
                <DialogContent>
                  <Box mb={2}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Time:
                    </Typography>
                    <Typography>
                      {format(selectedMeeting.start, 'PPp')} -{' '}
                      {format(selectedMeeting.end, 'p')}
                    </Typography>
                  </Box>
                  {selectedMeeting.description && (
                    <Box>
                      <Typography variant="subtitle1" color="textSecondary">
                        Description:
                      </Typography>
                      <Typography>{selectedMeeting.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle1" color="textSecondary">
                      Owner:
                    </Typography>
                    <Typography>{selectedMeeting.owner}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="textSecondary">
                      Status:
                    </Typography>
                    <Typography>{selectedMeeting.status}</Typography>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSelectedMeeting(null)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* New Meeting Dialog */}
          <Dialog
            open={isNewMeetingDialogOpen}
            onClose={() => setIsNewMeetingDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
          >
            <DialogTitle>Schedule New Meeting</DialogTitle>
            <DialogContent>
              <Box mb={2} mt={1}>
                <TextField
                  fullWidth
                  label="Meeting Title"
                  value={newMeeting.title || ''}
                  onChange={(e) =>
                    setNewMeeting({ ...newMeeting, title: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={newMeeting.description || ''}
                  onChange={(e) =>
                    setNewMeeting({ ...newMeeting, description: e.target.value })
                  }
                  multiline
                  rows={3}
                  margin="normal"
                />
                <Typography variant="subtitle2" color="textSecondary" mt={2}>
                  Time: {newMeeting.start && format(newMeeting.start, 'PPp')} -{' '}
                  {newMeeting.end && format(newMeeting.end, 'p')}
                </Typography>
                <Box>
                  <Typography variant="subtitle1" color="textSecondary">
                    Owner:
                  </Typography>
                  <Typography>{userName}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" color="textSecondary">
                    Team:
                  </Typography>
                  <Typography>{currentTeam}</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsNewMeetingDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateMeeting}
                variant="contained"
                disabled={!newMeeting.title}
              >
                Schedule
              </Button>
            </DialogActions>
          </Dialog>
        </ContentContainer>
      </MainContent>
    </PageLayout>
  );
};

export default MeetingsPage;
